/**
 * First-person right arm anchored to the camera (Minecraft style):
 * skin + sleeve box in the lower-right corner, walk bob, and a
 * one-shot swing animation on attack/break/place/eat.
 * Shows a small cube of the selected block in the hand.
 */

import * as THREE from "three";
import { isPlaceableBlock, BlockType } from "@craftgaming/shared";
import { getTextureAtlas } from "./texture-atlas";
import { getSlotIcon } from "../ui/hotbar";

const REST_POSITION = new THREE.Vector3(0.42, -0.42, -0.65);
const REST_ROTATION = new THREE.Euler(-0.25, 0.35, 0.1);
const SWING_DURATION = 0.28;

export type FirstPersonArm = {
  /** dt + horizontal speed (for walk bob). */
  update: (dt: number, speed: number) => void;
  swing: () => void;
  setHeldItem: (id: number) => void;
  setVisible: (visible: boolean) => void;
  dispose: () => void;
};

function buildArmMesh(skinColor: string, sleeveColor: string): THREE.Group {
  const group = new THREE.Group();

  // depthTest off — the arm is an overlay and must NEVER clip into
  // world geometry when the camera touches a wall
  const skin = new THREE.MeshBasicMaterial({ color: skinColor, depthTest: false });
  const sleeve = new THREE.MeshBasicMaterial({ color: sleeveColor, depthTest: false });

  const forearm = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 0.45), skin);
  forearm.position.z = -0.1;
  forearm.renderOrder = 999;
  group.add(forearm);

  const sleeveMesh = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.2), sleeve);
  sleeveMesh.position.z = 0.12;
  sleeveMesh.renderOrder = 999;
  group.add(sleeveMesh);

  return group;
}

export function createFirstPersonArm(
  camera: THREE.PerspectiveCamera,
  scene: THREE.Scene,
  colors: { skin: string; sleeve: string }
): FirstPersonArm {
  // Camera must be part of the scene for its children to render
  scene.add(camera);

  const root = new THREE.Group();
  root.position.copy(REST_POSITION);
  root.rotation.copy(REST_ROTATION);
  camera.add(root);

  const arm = buildArmMesh(colors.skin, colors.sleeve);
  root.add(arm);

  // Held BLOCK: a big isometric cube that REPLACES the hand (Minecraft
  // first-person style — the block dominates the corner).
  const heldMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false });
  const held = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.36, 0.36), heldMaterial);
  held.position.set(0.04, -0.02, -0.24);
  held.rotation.set(-0.32, 0.7, 0.0); // show top + two sides
  held.renderOrder = 1000;
  held.visible = false;
  root.add(held);

  // Held ITEM (tool / stick / food): a flat angled sprite, Minecraft-style
  const flatMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    alphaTest: 0.5,
    depthTest: false,
    side: THREE.DoubleSide,
  });
  // Big diagonal sprite like Minecraft holds a tool/sword
  const heldFlat = new THREE.Mesh(new THREE.PlaneGeometry(0.5, 0.5), flatMaterial);
  heldFlat.position.set(0.08, 0.02, -0.32);
  heldFlat.rotation.set(0.15, 0.0, -0.8); // blade points up-right
  heldFlat.renderOrder = 1000;
  heldFlat.visible = false;
  root.add(heldFlat);

  const textureLoader = new THREE.TextureLoader();
  const textureCache = new Map<number, THREE.Texture>();
  let currentHeld = 0;

  function loadIcon(id: number, forBlock: boolean): THREE.Texture {
    let texture = textureCache.get(id);
    if (!texture) {
      const dataUrl = forBlock
        ? getTextureAtlas().getBlockPreview(id, 32)
        : getSlotIcon(id, 32);
      texture = textureLoader.load(dataUrl, undefined, undefined, () => {
        console.warn(`[FirstPersonArm] Failed to load held texture for ${id}`);
      });
      texture.magFilter = THREE.NearestFilter;
      texture.colorSpace = THREE.SRGBColorSpace;
      textureCache.set(id, texture);
    }
    return texture;
  }

  function setHeldItem(id: number) {
    if (id === currentHeld) return;
    currentHeld = id;

    if (id === BlockType.AIR) {
      held.visible = false;
      heldFlat.visible = false;
      arm.visible = true; // empty hand
      return;
    }

    if (isPlaceableBlock(id)) {
      // Block → big cube replaces the hand
      heldMaterial.map = loadIcon(id, true);
      heldMaterial.needsUpdate = true;
      held.visible = true;
      heldFlat.visible = false;
      arm.visible = false;
    } else {
      // Tool / stick / food → flat sprite held in the visible hand
      flatMaterial.map = loadIcon(id, false);
      flatMaterial.needsUpdate = true;
      heldFlat.visible = true;
      held.visible = false;
      arm.visible = true;
    }
  }

  let swingTimer = 0;
  let bobPhase = 0;
  let visible = true;

  function swing() {
    swingTimer = SWING_DURATION;
  }

  function update(dt: number, speed: number) {
    if (!visible) return;

    // Walk bob
    if (speed > 0.2) {
      bobPhase += dt * Math.min(speed, 8) * 1.8;
    }
    const bobX = Math.sin(bobPhase) * 0.012;
    const bobY = -Math.abs(Math.cos(bobPhase)) * 0.015;

    if (swingTimer > 0) {
      swingTimer = Math.max(0, swingTimer - dt);
      const t = 1 - swingTimer / SWING_DURATION; // 0 → 1
      const arc = Math.sin(t * Math.PI);
      root.position.set(
        REST_POSITION.x - arc * 0.25,
        REST_POSITION.y - arc * 0.12 + bobY,
        REST_POSITION.z - arc * 0.25
      );
      root.rotation.set(
        REST_ROTATION.x - arc * 1.1,
        REST_ROTATION.y + arc * 0.4,
        REST_ROTATION.z
      );
    } else {
      root.position.set(REST_POSITION.x + bobX, REST_POSITION.y + bobY, REST_POSITION.z);
      root.rotation.copy(REST_ROTATION);
    }
  }

  function setVisible(v: boolean) {
    visible = v;
    root.visible = v;
  }

  function dispose() {
    camera.remove(root);
    root.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        (obj.material as THREE.Material).dispose();
      }
    });
    for (const texture of textureCache.values()) texture.dispose();
    textureCache.clear();
  }

  return { update, swing, setHeldItem, setVisible, dispose };
}
